import { useQuery } from "@tanstack/react-query";

import api from "api";
import { SOME_RIDICULOUS_NUMBER } from "constants/api";
import { ReactQueryKeys } from "constants/keys";
import { PatientAppointment } from "../PatientAppointments.types";

const MONTHS_BEFORE_CURRENT = 1;
const MONTHS_AFTER_CURRENT = 1;
const APPOINTMENTS_STALE_TIME_MS = 5 * 60 * 1000;

const formatDateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1);

const buildAppointmentWindows = (today = new Date()) => {
  const windows: Array<{ scheduledAfter: string; scheduledBefore: string }> = [];

  for (let index = MONTHS_BEFORE_CURRENT; index >= 1; index -= 1) {
    const month = addMonths(today, -index);
    windows.push({
      scheduledAfter: formatDateParam(startOfMonth(month)),
      scheduledBefore: formatDateParam(endOfMonth(month)),
    });
  }

  windows.push({
    scheduledAfter: formatDateParam(startOfMonth(today)),
    scheduledBefore: formatDateParam(endOfMonth(today)),
  });

  for (let index = 1; index <= MONTHS_AFTER_CURRENT; index += 1) {
    const month = addMonths(today, index);
    windows.push({
      scheduledAfter: formatDateParam(startOfMonth(month)),
      scheduledBefore: formatDateParam(endOfMonth(month)),
    });
  }

  return windows;
};

const dedupeAppointments = (appointments: PatientAppointment[]) => {
  const appointmentsById = new Map<string | number, PatientAppointment>();

  appointments.forEach((appointment) => {
    const key = appointment.id || `${appointment.start}-${appointment.appointment_type}-${appointment.calendar}`;
    appointmentsById.set(key, appointment);
  });

  return Array.from(appointmentsById.values());
};

export const fetchPatientAppointments = async (patientId: string): Promise<PatientAppointment[]> => {
  const baseParams = {
    page: 1,
    size: SOME_RIDICULOUS_NUMBER,
    attendeeIds: [patientId],
  };

  const responses = await Promise.all(
    buildAppointmentWindows().map((window) =>
      api.client.Appointment.list({
        ...baseParams,
        ...window,
      })
    )
  );

  return dedupeAppointments(responses.flatMap((response) => response?.body ?? []));
};

export const usePatientAppointments = (patientId?: string) => {
  return useQuery(
    [ReactQueryKeys.APPOINTMENTS, "patient", patientId],
    () => fetchPatientAppointments(patientId as string),
    {
      enabled: Boolean(patientId),
      staleTime: APPOINTMENTS_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  );
};
